import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { employeesService } from "../services/employees.service";
import type {
	CreateEmployeeInput,
	Employee,
	EmployeeListFilters,
	EmployeeListItem,
	UpdateEmployeeInput,
} from "../types";
import type { AttendanceDayLike } from "../utils/summary";

export function useEmployeesList(filters: EmployeeListFilters) {
	const params: Record<string, string> = {};
	if (filters.departmentId) {
		params.departmentId = filters.departmentId;
		// ⚠️ SATR (`"true"`) sifatida va FAQAT yoqilganda — backend
		// `z.stringbool()` bilan qabul qiladi, `false` da kalit umuman
		// uzatilmaydi (`buildEmployeeParams` dagi `riskFlagged` konvensiyasi).
		// `departmentId` siz backend uni baribir e'tiborsiz qoldiradi.
		if (filters.includeDescendants) {
			params.includeDescendants = "true";
		}
	}
	if (filters.positionId) {
		params.positionId = filters.positionId;
	}
	if (filters.hrStatus) {
		params.hrStatus = filters.hrStatus;
	}
	if (filters.q) {
		params.q = filters.q;
	}

	return usePaginatedQuery<EmployeeListItem>({
		queryKey: ["employees"],
		url: "/employees",
		params: Object.keys(params).length > 0 ? params : undefined,
	});
}

interface DepartmentOption {
	id: string;
	name: string;
}

/**
 * Bo'lim tanlash uchun — `AssignRoleModal` (users moduli) naqshiga ergashib,
 * boshqa modulning servisini import qilish o'rniga to'g'ridan-to'g'ri
 * `apiClient` orqali so'raladi. Bo'limlar sahifalanmagan (`success()`).
 */
export function useDepartmentOptions() {
	return useQuery({
		queryKey: ["departments", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: DepartmentOption[] }>(
				"/departments"
			);
			return response.data.data;
		},
		staleTime: 60_000,
	});
}

interface PositionOption {
	id: string;
	name: string;
}

/**
 * Lavozimlar ro'yxati. `departmentId` berilsa — faqat shu bo'lim darajasiga
 * biriktirilgan lavozimlar (`GET /positions?departmentId=`, roster).
 *
 * ⚠️ Filtrsiz kalit AYNAN `["positions", "all"]` bo'lib qoladi — jadvallar
 * (`EmployeesPage`, `DepartmentEmployeesTab`) lavozim NOMINI shu keshdan
 * oladi va testlar shu kalitni urug'laydi.
 */
export function usePositionOptions(departmentId?: string) {
	return useQuery({
		queryKey: departmentId ? ["positions", "byDepartment", departmentId] : ["positions", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: PositionOption[] }>(
				"/positions",
				departmentId ? { params: { departmentId } } : undefined
			);
			return response.data.data;
		},
		staleTime: 60_000,
	});
}

/**
 * Rahbar tanlash uchun nomzodlar — server tomonida qidiriladi (`q` filtri,
 * `fullName` bo'yicha). Xodimlar 100 tadan ko'p bo'lishi mumkin (bank
 * miqyosida odatiy holat), shuning uchun bitta sahifani mijozda filtrlash
 * o'rniga har qidiruv matni backend'ga yuboriladi — `search` bo'sh bo'lsa
 * standart birinchi 100 ta xodim ko'rsatiladi (forma ochilganda bo'sh
 * ro'yxat ko'rinmasin uchun). Debounce chaqiruvchi tomonda (`EmployeeFormModal`).
 */
export function useManagerOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "manager-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };
			if (search.trim()) {
				params.q = search.trim();
			}
			const response = await apiClient.get<{
				success: true;
				data: { items: Employee[] };
			}>("/employees", { params });
			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

export function useEmployeeDetail(id: string | undefined) {
	return useQuery({
		queryKey: ["employees", "detail", id],
		queryFn: () => employeesService.getById(id as string),
		enabled: Boolean(id),
	});
}

/**
 * Profilning 30 kunlik oynasi uchun davomat kunlari.
 *
 * ⚠️ **`modules/attendance` ning hook'i import QILINMAYDI** — modul
 * chegarasi: davomat modulidagi filtr yoki sahifalash o'zgarishi profil
 * sahifasini sindirmasligi kerak (`modules/dashboard` da xuddi shu
 * naqsh). Bu yerda so'rov to'g'ridan-to'g'ri quriladi.
 *
 * ⚠️ `limit: 100` — oyna 30 kun, ya'ni bitta sahifa YETARLI va
 * sahifalash umuman kerak emas.
 *
 * ⚠️⚠️ **Kesh kaliti `"attendance-days"` bilan BOSHLANADI, `"employees"`
 * bilan emas.** Kun tuzatilganda `useCorrectAttendanceDay`
 * (`modules/attendance`) `invalidateQueries({ queryKey:
 * ["attendance-days"] })` chaqiradi, TanStack Query esa kalitni
 * BOSHIDAN prefiks bo'yicha solishtiradi — `["employees",
 * "attendance-days", …]` bu invalidatsiyaga MOS KELMASDI va tuzatishdan
 * keyin profil eski holatni ko'rsatib turaverardi (jimgina).
 */
/**
 * Profil oynasidagi bir kun.
 *
 * ⚠️⚠️ **`AttendanceDayLike` dan KENGROQ va bu ataylab.** «Umumiy»
 * yorlig'idagi jadval qatori bosilganda `DayDetailDrawer` ochiladi, u esa
 * `attendance_days.id` ni kutadi — `AttendanceDayLike` da `id` YO'Q.
 * Maydon `GET /attendance/days` javobida ALLAQACHON bor
 * (`AttendanceDaySchema`), faqat lokal tip uni sanamasdi.
 *
 * ⚠️ `AttendanceDay` (`modules/attendance/types`) to'liq import
 * QILINMAYDI — profil unga qaram bo'lib qolardi va davomat modulidagi
 * har tip o'zgarishi profilni sindirardi (`summary.ts:1-8` da AYNI
 * naqsh ataylab tanlangan). Bu yerda faqat KERAKLI kengaytma e'lon
 * qilinadi.
 */
export type EmployeeAttendanceDay = AttendanceDayLike & {
	id: string;
	firstCheckInAt: string | null;
	lastCheckOutAt: string | null;
	timeInZoneMin: number;
};

export function useEmployeeAttendanceDays(
	employeeId: string | undefined,
	from: string,
	to: string
) {
	return useQuery({
		queryKey: ["attendance-days", "employee-window", employeeId, from, to],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeAttendanceDay[] };
			}>("/attendance/days", {
				params: { employeeId, from, to, page: 1, limit: 100 },
			});

			return response.data.data.items;
		},
		enabled: Boolean(employeeId),
		staleTime: 60_000,
	});
}

export function useCreateEmployee() {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: CreateEmployeeInput) => employeesService.create(input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["employees"] });
			message.success(t("messages.created"));
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.createError")));
		},
	});
}

export function useUpdateEmployee() {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: UpdateEmployeeInput }) =>
			employeesService.update(id, input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["employees"] });
			message.success(t("messages.updated"));
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.updateError")));
		},
	});
}

export function useSetEmployeePhoto() {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, file }: { id: string; file: File }) => employeesService.setPhoto(id, file),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["employees"] });
			message.success(t("messages.photoAttached"));
		},
		onError: (error) => {
			// 503 uchun alohida matn (spec §10). Umumiy `getErrorMessage` bu
			// holatda serverning «Yuz tanish xizmati vaqtincha mavjud emas»
			// xabarini qaytaradi, lekin foydalanuvchiga eng muhimi — **xodim
			// o'zgarmagani**: qayta urinish xavfsiz, hech narsa yarim holatda
			// qolmadi (spec §4, 2-qadam).
			if (isAxiosError(error) && error.response?.status === 503) {
				message.error(t("messages.faceAiUnavailable"));
				return;
			}

			// 422 — «yuz aniq ko'rinmadi yoki bittadan ko'p». Serverning AYNAN
			// shu xabari ko'rsatiladi: umumiy matn adminga rasmni nima uchun
			// almashtirish kerakligini aytmasdi.
			message.error(getErrorMessage(error, t("messages.photoAttachError")));
		},
	});
}

export function useDeleteEmployeePhoto() {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => employeesService.deletePhoto(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["employees"] });
			message.success(t("messages.photoRemoved"));
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.photoRemoveError")));
		},
	});
}

/**
 * Rasm tarkibini blob URL sifatida beradi.
 *
 * ⚠️ **TanStack Query ATAYLAB ishlatilmadi.** Blob URL — bo'shatilishi shart
 * bo'lgan resurs; uni query keshida saqlash «kesh hali tirik, lekin URL
 * allaqachon `revokeObjectURL` qilingan» holatini keltirib chiqarardi va
 * rasm boshqa mount'da jimgina buzilardi. `useEffect` egaligi esa aniq:
 * URL shu effekt ichida yaratiladi va shu yerda bo'shatiladi.
 *
 * `photoFileId` — kalit: u o'zgarganda (rasm almashtirildi/o'chirildi) effekt
 * qayta ishlaydi. `null` bo'lsa so'rov umuman yuborilmaydi.
 */
export function useEmployeePhotoUrl(employeeId: string | null, photoFileId: string | null) {
	const [url, setUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!(employeeId && photoFileId)) {
			setUrl(null);
			return;
		}

		let bekorQilindi = false;
		let yaratilgan: string | null = null;

		employeesService
			.getPhotoBlob(employeeId)
			.then((blob) => {
				if (bekorQilindi) {
					return;
				}
				yaratilgan = URL.createObjectURL(blob);
				setUrl(yaratilgan);
			})
			.catch(() => {
				// 404 — rasm yo'q (fayl qatori o'chirilgan bo'lishi mumkin).
				// Ko'rsatiladigan narsa yo'q, xato xabari ham keraksiz.
				if (!bekorQilindi) {
					setUrl(null);
				}
			});

		return () => {
			bekorQilindi = true;
			if (yaratilgan) {
				URL.revokeObjectURL(yaratilgan);
			}
		};
	}, [employeeId, photoFileId]);

	return url;
}
